import { SUBTYPE_OPTS, time, getSuper, bindPrototype, assignPrivateSymbol, setSymbols, getSymbols, iu, initAbstract, construct, $combine, subtype, implementEngineer, $skip, makeClassSymbol } from '@type.engineering/type-engineer'
import '../'
import '../typedefs/private'

/**
 * @abstract
 * @extends {guest.maurice.Stateful<STATE>}
 * @template STATE
 */
class _AbstractStateful {
  constructor() {
    construct(this)
  }
}
/**
 * Stores the state of the component in a `state` property.
 * @extends {guest.maurice.Stateful<STATE>} ‎
 * @implements {guest.maurice.IStateful<STATE>} ‎
 * @implements {engineering.type.IInitialiser<guest.maurice.IStateful, guest.maurice.IStateful.Initialese<STATE>>} ‎
 * @template STATE
 */
export class AbstractStateful extends _AbstractStateful {

}
initAbstract(_AbstractStateful, 'IStateful', /** @this {AbstractStateful} */ function () {
  const { _state, _initialState } = getStatefulSymbols(this)
  setStatefulSymbols(this, {
    _state: iu(_state, {}),
  })
})
/** @constructor @extends {_AbstractStateful} @suppress {checkTypes} */ function __AbstractStateful() {}
__AbstractStateful.prototype = /** @type {!_AbstractStateful} */ ({
  get state() {
    return this[StatefulSymbols._state]
  },
  get initialState() {
    return this[StatefulSymbols._initialState]
  },
  get asIStateful() {
    return /** @type {!guest.maurice.BoundIStateful<STATE>} */ (this)
  },
  get superIStateful() {
    return /** @type {!guest.maurice.BoundIStateful<STATE>} */ (bindPrototype(_AbstractStateful, this))
  },
  get superStateful() {
    return /** @type {!guest.maurice.BoundStateful<STATE>} */ (bindPrototype(_AbstractStateful, this))
  },
})
export const StatefulSymbols = {
  _state: makeClassSymbol(AbstractStateful, '_state'),
  _initialState: makeClassSymbol(AbstractStateful, '_initialState'),
}

/** @type {guest.maurice.getStatefulSymbols} */
export const getStatefulSymbols = (instance, _STATE) => {
  return /** @type {!guest.maurice.IStatefulSymbolsOut} */ (getSymbols(instance, StatefulSymbols))
}
/** @type {guest.maurice.setStatefulSymbols} */
export const setStatefulSymbols = (instance, symbols, _STATE) => {
  setSymbols(instance, symbols, StatefulSymbols)
}

AbstractStateful.__implement(__AbstractStateful)

/**
 * @abstract
 * @extends {guest.maurice.Proper<PROPS>}
 * @template PROPS
 */
class _AbstractProper {
  constructor() {
    construct(this)
  }
}
/**
 * Records the properties of the building block. These are received from the
 * construction site such as HTML page.
 * @extends {guest.maurice.Proper<PROPS>} ‎
 * @implements {guest.maurice.IProper<PROPS>} ‎
 * @implements {engineering.type.IInitialiser<guest.maurice.IProper, guest.maurice.IProper.Initialese<PROPS>>} ‎
 * @template PROPS
 */
export class AbstractProper extends _AbstractProper {

}
initAbstract(_AbstractProper, 'IProper', /** @this {AbstractProper} */ function () {
  const { _props, _initialProps } = getProperSymbols(this)
  setProperSymbols(this, {
    _props: iu(_props, {}),
  })
})
/** @constructor @extends {_AbstractProper} @suppress {checkTypes} */ function __AbstractProper() {}
__AbstractProper.prototype = /** @type {!_AbstractProper} */ ({
  get props() {
    return this[ProperSymbols._props]
  },
  get initialProps() {
    return this[ProperSymbols._initialProps]
  },
  get asIProper() {
    return /** @type {!guest.maurice.BoundIProper<PROPS>} */ (this)
  },
  get superIProper() {
    return /** @type {!guest.maurice.BoundIProper<PROPS>} */ (bindPrototype(_AbstractProper, this))
  },
  get superProper() {
    return /** @type {!guest.maurice.BoundProper<PROPS>} */ (bindPrototype(_AbstractProper, this))
  },
})
export const ProperSymbols = {
  _props: makeClassSymbol(AbstractProper, '_props'),
  _initialProps: makeClassSymbol(AbstractProper, '_initialProps'),
}

/** @type {guest.maurice.getProperSymbols} */
export const getProperSymbols = (instance, _PROPS) => {
  return /** @type {!guest.maurice.IProperSymbolsOut} */ (getSymbols(instance, ProperSymbols))
}
/** @type {guest.maurice.setProperSymbols} */
export const setProperSymbols = (instance, symbols, _PROPS) => {
  setSymbols(instance, symbols, ProperSymbols)
}

AbstractProper.__implement(__AbstractProper)

/**
 * @abstract
 * @extends {guest.maurice.BuildingBlock<BLOCK_STATE, BLOCK_PROPS>}
 * @template BLOCK_STATE
 * @template BLOCK_PROPS
 */
class _AbstractBuildingBlock {
  /** @param {!guest.maurice.IBuildingBlock.Initialese<BLOCK_STATE, BLOCK_PROPS>} init */
  constructor(init) {
    construct(this)
    this.initialiser(init)
  }
}
/**
 * A building block of an application.
 * @extends {guest.maurice.BuildingBlock<BLOCK_STATE, BLOCK_PROPS>} ‎
 * @implements {guest.maurice.IBuildingBlock<BLOCK_STATE, BLOCK_PROPS>} ‎
 * @implements {engineering.type.IInitialiser<guest.maurice.IBuildingBlock, guest.maurice.IBuildingBlock.Initialese<BLOCK_STATE, BLOCK_PROPS>>} ‎
 * @template BLOCK_STATE
 * @template BLOCK_PROPS
 */
export class AbstractBuildingBlock extends _AbstractBuildingBlock {

}
initAbstract(_AbstractBuildingBlock, 'IBuildingBlock')
/** @constructor @extends {_AbstractBuildingBlock} @suppress {checkTypes} */ function __AbstractBuildingBlock() {}
__AbstractBuildingBlock.prototype = /** @type {!_AbstractBuildingBlock} */ ({
  get asIBuildingBlock() {
    return /** @type {!guest.maurice.BoundIBuildingBlock<BLOCK_STATE, BLOCK_PROPS>} */ (this)
  },
  get superIBuildingBlock() {
    return /** @type {!guest.maurice.BoundIBuildingBlock<BLOCK_STATE, BLOCK_PROPS>} */ (bindPrototype(_AbstractBuildingBlock, this))
  },
  get superBuildingBlock() {
    return /** @type {!guest.maurice.BoundBuildingBlock<BLOCK_STATE, BLOCK_PROPS>} */ (bindPrototype(_AbstractBuildingBlock, this))
  },
})
export const BuildingBlockSymbols = {
  __: makeClassSymbol(AbstractBuildingBlock, '__'),
}

/** @type {guest.maurice.getBuildingBlockSymbols} */
export const getBuildingBlockSymbols = (instance) => {
  return /** @type {!guest.maurice.IBuildingBlockSymbolsOut} */ (getSymbols(instance, BuildingBlockSymbols))
}
/** @type {guest.maurice.setBuildingBlockSymbols} */
export const setBuildingBlockSymbols = (instance, symbols) => {
  setSymbols(instance, symbols, BuildingBlockSymbols)
}

AbstractBuildingBlock.__implement(__AbstractBuildingBlock)

/**
 * @abstract
 * @extends {guest.maurice.Dep}
 */
class _AbstractDep {
  constructor() {
    construct(this)
  }
}
/**
 * _Deprecated_. Allows to wait for all block's dependencies to load. Applies
 * to the blocks only.
 * @extends {guest.maurice.Dep} ‎
 * @implements {guest.maurice.IDep} ‎
 */
export class AbstractDep extends _AbstractDep {

}
initAbstract(_AbstractDep, 'IDep', /** @this {AbstractDep} */ function () {
  /** @type {boolean} */ this.__$childConstructed = false
})
/** @constructor @extends {_AbstractDep} @suppress {checkTypes} */ function __AbstractDep() {}
__AbstractDep.prototype = /** @type {!_AbstractDep} */ ({
  get asIDep() {
    return /** @type {!guest.maurice.BoundIDep} */ (this)
  },
  get superIDep() {
    return /** @type {!guest.maurice.BoundIDep} */ (bindPrototype(_AbstractDep, this))
  },
  get superDep() {
    return /** @type {!guest.maurice.BoundDep} */ (bindPrototype(_AbstractDep, this))
  },
})
AbstractDep.__implement(__AbstractDep)
/**
 * @abstract
 * @extends {guest.maurice.Elemental}
 */
class _AbstractElemental {
  constructor() {
    construct(this)
  }
}
/**
 * Contains a reference to the `element`.
 * @extends {guest.maurice.Elemental} ‎
 * @implements {guest.maurice.IElemental} ‎
 */
export class AbstractElemental extends _AbstractElemental {

}
initAbstract(_AbstractElemental, 'IElemental')
/** @constructor @extends {_AbstractElemental} @suppress {checkTypes} */ function __AbstractElemental() {}
__AbstractElemental.prototype = /** @type {!_AbstractElemental} */ ({
  get element() {
    return this[ElementalSymbols._element]
  },
  get asIElemental() {
    return /** @type {!guest.maurice.BoundIElemental} */ (this)
  },
  get superIElemental() {
    return /** @type {!guest.maurice.BoundIElemental} */ (bindPrototype(_AbstractElemental, this))
  },
  get superElemental() {
    return /** @type {!guest.maurice.BoundElemental} */ (bindPrototype(_AbstractElemental, this))
  },
})
export const ElementalSymbols = {
  _element: makeClassSymbol(AbstractElemental, '_element'),
}

/** @type {guest.maurice.getElementalSymbols} */
export const getElementalSymbols = (instance) => {
  return /** @type {!guest.maurice.IElementalSymbolsOut} */ (getSymbols(instance, ElementalSymbols))
}
/** @type {guest.maurice.setElementalSymbols} */
export const setElementalSymbols = (instance, symbols) => {
  setSymbols(instance, symbols, ElementalSymbols)
}

AbstractElemental.__implement(__AbstractElemental)
/**
 * @abstract
 * @extends {guest.maurice.ElementalDep}
 */
class _AbstractElementalDep {
  /** @param {!guest.maurice.IElementalDep.Initialese} init */
  constructor(init) {
    construct(this)
    this.initialiser(init)
  }
}
/**
 * Allows to wait for all block's dependencies to load. Applies
 * to the blocks only.
 * @extends {guest.maurice.ElementalDep} ‎
 * @implements {guest.maurice.IElementalDep} ‎
 * @implements {engineering.type.IInitialiser<guest.maurice.IElementalDep, guest.maurice.IElementalDep.Initialese>} ‎
 */
export class AbstractElementalDep extends _AbstractElementalDep {

}
initAbstract(_AbstractElementalDep, 'IElementalDep', /** @this {AbstractElementalDep} */ function () {
  /** @type {HTMLElement} */ this.combinationElement = null
  /** @type {guest.maurice.IElementalDep} */ this.combinator = null
  /** @type {!Array<!guest.maurice.IElementalDep>} */ this.deps = []
  /** @type {boolean} */ this.__$childConstructed = false
})
/** @constructor @extends {_AbstractElementalDep} @suppress {checkTypes} */ function __AbstractElementalDep() {}
__AbstractElementalDep.prototype = /** @type {!_AbstractElementalDep} */ ({
  get asIElementalDep() {
    return /** @type {!guest.maurice.BoundIElementalDep} */ (this)
  },
  get superIElementalDep() {
    return /** @type {!guest.maurice.BoundIElementalDep} */ (bindPrototype(_AbstractElementalDep, this))
  },
  get superElementalDep() {
    return /** @type {!guest.maurice.BoundElementalDep} */ (bindPrototype(_AbstractElementalDep, this))
  },
})
AbstractElementalDep.__implement(__AbstractElementalDep)

/**
 * @abstract
 * @extends {guest.maurice.SuperEventListener}
 */
class _AbstractSuperEventListener {
  constructor() {
    construct(this)
  }
}
/**
 * An event listener that wraps the original event listener on an element.
 * @extends {guest.maurice.SuperEventListener} ‎
 * @implements {guest.maurice.ISuperEventListener} ‎
 */
export class AbstractSuperEventListener extends _AbstractSuperEventListener {

}
initAbstract(_AbstractSuperEventListener, 'ISuperEventListener', /** @this {AbstractSuperEventListener} */ function () {
  const { _eventListeners, _superEventListeners } = getSuperEventListenerSymbols(this)
  setSuperEventListenerSymbols(this, {
    _eventListeners: iu(_eventListeners, {}),
    _superEventListeners: iu(_superEventListeners, {}),
  })
})
/** @constructor @extends {_AbstractSuperEventListener} @suppress {checkTypes} */ function __AbstractSuperEventListener() {}
__AbstractSuperEventListener.prototype = /** @type {!_AbstractSuperEventListener} */ ({
  get eventListeners() {
    return this[SuperEventListenerSymbols._eventListeners]
  },
  get superEventListeners() {
    return this[SuperEventListenerSymbols._superEventListeners]
  },
  get asISuperEventListener() {
    return /** @type {!guest.maurice.BoundISuperEventListener} */ (this)
  },
  get superISuperEventListener() {
    return /** @type {!guest.maurice.BoundISuperEventListener} */ (bindPrototype(_AbstractSuperEventListener, this))
  },
  get superSuperEventListener() {
    return /** @type {!guest.maurice.BoundSuperEventListener} */ (bindPrototype(_AbstractSuperEventListener, this))
  },
})
export const SuperEventListenerSymbols = {
  _eventListeners: makeClassSymbol(AbstractSuperEventListener, '_eventListeners'),
  _superEventListeners: makeClassSymbol(AbstractSuperEventListener, '_superEventListeners'),
}

/** @type {guest.maurice.getSuperEventListenerSymbols} */
export const getSuperEventListenerSymbols = (instance) => {
  return /** @type {!guest.maurice.ISuperEventListenerSymbolsOut} */ (getSymbols(instance, SuperEventListenerSymbols))
}
/** @type {guest.maurice.setSuperEventListenerSymbols} */
export const setSuperEventListenerSymbols = (instance, symbols) => {
  setSymbols(instance, symbols, SuperEventListenerSymbols)
}

AbstractSuperEventListener.__implement(__AbstractSuperEventListener)

// /** @abstract {guest.maurice.IState} */
// export class AbstractState {}

/**
 * @abstract
 * @extends {guest.maurice.Seer}
 */
class _AbstractSeer {
  constructor() {
    construct(this)
  }
}
/**
 * Sees changes in an object.
 * @extends {guest.maurice.Seer} ‎
 * @implements {guest.maurice.ISeer} ‎
 */
export class AbstractSeer extends _AbstractSeer {

}
initAbstract(_AbstractSeer, 'ISeer')
/** @constructor @extends {_AbstractSeer} @suppress {checkTypes} */ function __AbstractSeer() {}
__AbstractSeer.prototype = /** @type {!_AbstractSeer} */ ({
  get asISeer() {
    return /** @type {!guest.maurice.BoundISeer} */ (this)
  },
  get superISeer() {
    return /** @type {!guest.maurice.BoundISeer} */ (bindPrototype(_AbstractSeer, this))
  },
  get superSeer() {
    return /** @type {!guest.maurice.BoundSeer} */ (bindPrototype(_AbstractSeer, this))
  },
})
AbstractSeer.__implement(__AbstractSeer)

/**
 * @abstract
 * @extends {guest.maurice.InstanceSeer}
 */
class _AbstractInstanceSeer extends /** @type {typeof AbstractInstanceSeer} */ (AbstractSeer) {
  constructor() {
    super()
    construct(this)
  }
}
/**
 * A seer that is attached to an instance that can be passed, when for example
 * data that the seer receives in the `__$see` method is a function that should
 * be executed with the instance as the `this` context.
 * @extends {guest.maurice.InstanceSeer} ‎
 * @implements {guest.maurice.IInstanceSeer} ‎
 */
export class AbstractInstanceSeer extends _AbstractInstanceSeer {

}
initAbstract(_AbstractInstanceSeer, 'IInstanceSeer', /** @this {AbstractInstanceSeer} */ function () {
  try { AbstractSeer.prototype.__$constructor.call(this) } catch (e) { /**/ }
  /** @type {Object} */ this.instance = null
})
/** @constructor @extends {_AbstractInstanceSeer} @suppress {checkTypes} */ function __AbstractInstanceSeer() {}
__AbstractInstanceSeer.prototype = /** @type {!_AbstractInstanceSeer} */ ({
  get asIInstanceSeer() {
    return /** @type {!guest.maurice.BoundIInstanceSeer} */ (this)
  },
  get superIInstanceSeer() {
    return /** @type {!guest.maurice.BoundIInstanceSeer} */ (bindPrototype(_AbstractInstanceSeer, this))
  },
  get superInstanceSeer() {
    return /** @type {!guest.maurice.BoundInstanceSeer} */ (bindPrototype(_AbstractInstanceSeer, this))
  },
})
AbstractInstanceSeer.__implement(__AbstractInstanceSeer)

/**
 * @abstract
 * @extends {guest.maurice.StatefulCbInstanceSeer}
 */
class _AbstractStatefulCbInstanceSeer extends /** @type {typeof AbstractStatefulCbInstanceSeer} */ (AbstractInstanceSeer) {
  constructor() {
    super()
    construct(this)
  }
}
/**
 * A seer that will call the callback received in data with the instance as
 * its binding context. This, for example, applies to the painter seer that
 * will call the `data` callback to repaint the instance after a change in
 * a certain state.
 * @extends {guest.maurice.StatefulCbInstanceSeer} ‎
 * @implements {guest.maurice.IStatefulCbInstanceSeer} ‎
 */
export class AbstractStatefulCbInstanceSeer extends _AbstractStatefulCbInstanceSeer {

}
initAbstract(_AbstractStatefulCbInstanceSeer, 'IStatefulCbInstanceSeer', /** @this {AbstractStatefulCbInstanceSeer} */ function () {
  try { AbstractInstanceSeer.prototype.__$constructor.call(this) } catch (e) { /**/ }
  /** @type {boolean} */ this.enableHoarding = true
  const { _hoard } = getStatefulCbInstanceSeerSymbols(this)
  setStatefulCbInstanceSeerSymbols(this, {
    _hoard: iu(_hoard, null),
  })
})
/** @constructor @extends {_AbstractStatefulCbInstanceSeer} @suppress {checkTypes} */ function __AbstractStatefulCbInstanceSeer() {}
__AbstractStatefulCbInstanceSeer.prototype = /** @type {!_AbstractStatefulCbInstanceSeer} */ ({
  get hoard() {
    return this[StatefulCbInstanceSeerSymbols._hoard]
  },
  get asIStatefulCbInstanceSeer() {
    return /** @type {!guest.maurice.BoundIStatefulCbInstanceSeer} */ (this)
  },
  get superIStatefulCbInstanceSeer() {
    return /** @type {!guest.maurice.BoundIStatefulCbInstanceSeer} */ (bindPrototype(_AbstractStatefulCbInstanceSeer, this))
  },
  get superStatefulCbInstanceSeer() {
    return /** @type {!guest.maurice.BoundStatefulCbInstanceSeer} */ (bindPrototype(_AbstractStatefulCbInstanceSeer, this))
  },
})
export const StatefulCbInstanceSeerSymbols = {
  _hoard: makeClassSymbol(AbstractStatefulCbInstanceSeer, '_hoard'),
}

/** @type {guest.maurice.getStatefulCbInstanceSeerSymbols} */
export const getStatefulCbInstanceSeerSymbols = (instance) => {
  return /** @type {!guest.maurice.IStatefulCbInstanceSeerSymbolsOut} */ (getSymbols(instance, StatefulCbInstanceSeerSymbols))
}
/** @type {guest.maurice.setStatefulCbInstanceSeerSymbols} */
export const setStatefulCbInstanceSeerSymbols = (instance, symbols) => {
  setSymbols(instance, symbols, StatefulCbInstanceSeerSymbols)
}

AbstractStatefulCbInstanceSeer.__implement(__AbstractStatefulCbInstanceSeer)

/**
 * @abstract
 * @extends {guest.maurice.Painter}
 */
class _AbstractPainter {
  constructor() {
    construct(this)
  }
}
/**
 *     Allows to update the presentation of the block on the screen based on
 * the current state.
 * @extends {guest.maurice.Painter} ‎
 * @implements {guest.maurice.IPainter} ‎
 */
export class AbstractPainter extends _AbstractPainter {

}
initAbstract(_AbstractPainter, 'IPainter')
/** @constructor @extends {_AbstractPainter} @suppress {checkTypes} */ function __AbstractPainter() {}
__AbstractPainter.prototype = /** @type {!_AbstractPainter} */ ({
  get paintSeer() {
    return this[PainterSymbols._paintSeer]
  },
  get asIPainter() {
    return /** @type {!guest.maurice.BoundIPainter} */ (this)
  },
  get superIPainter() {
    return /** @type {!guest.maurice.BoundIPainter} */ (bindPrototype(_AbstractPainter, this))
  },
  get superPainter() {
    return /** @type {!guest.maurice.BoundPainter} */ (bindPrototype(_AbstractPainter, this))
  },
})
export const PainterSymbols = {
  _paintSeer: makeClassSymbol(AbstractPainter, '_paintSeer'),
}

/** @type {guest.maurice.getPainterSymbols} */
export const getPainterSymbols = (instance) => {
  return /** @type {!guest.maurice.IPainterSymbolsOut} */ (getSymbols(instance, PainterSymbols))
}
/** @type {guest.maurice.setPainterSymbols} */
export const setPainterSymbols = (instance, symbols) => {
  setSymbols(instance, symbols, PainterSymbols)
}

AbstractPainter.__implement(__AbstractPainter)

/**
 * @abstract
 * @extends {guest.maurice.Classified}
 */
class _AbstractClassified {
  constructor() {
    construct(this)
  }
}
/**
 * Represents objects that can have HTML classes.
 * @extends {guest.maurice.Classified} ‎
 * @implements {guest.maurice.IClassified} ‎
 * @implements {engineering.type.IInitialiser<guest.maurice.IClassified, guest.maurice.IClassified.Initialese>} ‎
 */
export class AbstractClassified extends _AbstractClassified {

}
initAbstract(_AbstractClassified, 'IClassified', /** @this {AbstractClassified} */ function () {
  const { _classes } = getClassifiedSymbols(this)
  setClassifiedSymbols(this, {
    _classes: iu(_classes, {}),
  })
})
/** @constructor @extends {_AbstractClassified} @suppress {checkTypes} */ function __AbstractClassified() {}
__AbstractClassified.prototype = /** @type {!_AbstractClassified} */ ({
  get classes() {
    return this[ClassifiedSymbols._classes]
  },
  get asIClassified() {
    return /** @type {!guest.maurice.BoundIClassified} */ (this)
  },
  get superIClassified() {
    return /** @type {!guest.maurice.BoundIClassified} */ (bindPrototype(_AbstractClassified, this))
  },
  get superClassified() {
    return /** @type {!guest.maurice.BoundClassified} */ (bindPrototype(_AbstractClassified, this))
  },
})
export const ClassifiedSymbols = {
  _classes: makeClassSymbol(AbstractClassified, '_classes'),
}

/** @type {guest.maurice.getClassifiedSymbols} */
export const getClassifiedSymbols = (instance) => {
  return /** @type {!guest.maurice.IClassifiedSymbolsOut} */ (getSymbols(instance, ClassifiedSymbols))
}
/** @type {guest.maurice.setClassifiedSymbols} */
export const setClassifiedSymbols = (instance, symbols) => {
  setSymbols(instance, symbols, ClassifiedSymbols)
}

AbstractClassified.__implement(__AbstractClassified)

/**
 * @abstract
 * @extends {guest.maurice.View<VIEW_PROPS, MODEL>}
 * @template VIEW_PROPS
 * @template MODEL
 */
class _AbstractView {
  constructor() {
    construct(this)
  }
}
/**
 * A view is responsible for visual representation of its model (and external
 * models on the screen). When painting with the `paint` method for the first
 * time, the _Guest_ seers mechanism will register the view as a dependent on a
 * model that has been proxified with `proxifyState`.
 * @extends {guest.maurice.View<VIEW_PROPS, MODEL>} ‎
 * @implements {guest.maurice.IView<VIEW_PROPS, MODEL>} ‎
 * @implements {engineering.type.IInitialiser<guest.maurice.IView, guest.maurice.IView.Initialese<VIEW_PROPS>>} ‎
 * @template VIEW_PROPS
 * @template MODEL
 */
export class AbstractView extends _AbstractView {

}
initAbstract(_AbstractView, 'IView')
/** @constructor @extends {_AbstractView} @suppress {checkTypes} */ function __AbstractView() {}
__AbstractView.prototype = /** @type {!_AbstractView} */ ({
  get model() {
    return this[ViewSymbols._model]
  },
  get asIView() {
    return /** @type {!guest.maurice.BoundIView<VIEW_PROPS, MODEL>} */ (this)
  },
  get superIView() {
    return /** @type {!guest.maurice.BoundIView<VIEW_PROPS, MODEL>} */ (bindPrototype(_AbstractView, this))
  },
  get superView() {
    return /** @type {!guest.maurice.BoundView<VIEW_PROPS, MODEL>} */ (bindPrototype(_AbstractView, this))
  },
})
export const ViewSymbols = {
  _model: makeClassSymbol(AbstractView, '_model'),
}

/** @type {guest.maurice.getViewSymbols} */
export const getViewSymbols = (instance, _MODEL) => {
  return /** @type {!guest.maurice.IViewSymbolsOut} */ (getSymbols(instance, ViewSymbols))
}
/** @type {guest.maurice.setViewSymbols} */
export const setViewSymbols = (instance, symbols, _MODEL) => {
  setSymbols(instance, symbols, ViewSymbols)
}

AbstractView.__implement(__AbstractView)

/**
 * @abstract
 * @extends {guest.maurice.Component<STATE, PROPS>}
 * @template STATE
 * @template PROPS
 */
class _AbstractComponent {
  /** @param {!guest.maurice.IComponent.Initialese<STATE, PROPS>} init */
  constructor(init) {
    construct(this)
    this.initialiser(init)
  }
}
/**
 * A component encapsulates the view (displaying data), model (the object with
 * data required by the view to paint) and view controller interface that
 * defines user inputs that a component can receive.
 *
 * A component implements a stateful interface which acts as a mechanism for
 * model handling. The model is passed to the view which isn't supposed that it
 * is part of a component and can only work with the model. The controller's
 * methods should be implemented by the component and can modify the model.
 * @extends {guest.maurice.Component<STATE, PROPS>} ‎
 * @implements {guest.maurice.IComponent<STATE, PROPS>} ‎
 * @implements {engineering.type.IInitialiser<guest.maurice.IComponent, guest.maurice.IComponent.Initialese<STATE, PROPS>>} ‎
 * @template STATE
 * @template PROPS
 */
export class AbstractComponent extends _AbstractComponent {

}
initAbstract(_AbstractComponent, 'IComponent')
/** @constructor @extends {_AbstractComponent} @suppress {checkTypes} */ function __AbstractComponent() {}
__AbstractComponent.prototype = /** @type {!_AbstractComponent} */ ({
  get asIComponent() {
    return /** @type {!guest.maurice.BoundIComponent<STATE, PROPS>} */ (this)
  },
  get superIComponent() {
    return /** @type {!guest.maurice.BoundIComponent<STATE, PROPS>} */ (bindPrototype(_AbstractComponent, this))
  },
  get superComponent() {
    return /** @type {!guest.maurice.BoundComponent<STATE, PROPS>} */ (bindPrototype(_AbstractComponent, this))
  },
})
AbstractComponent.__implement(__AbstractComponent)
